import React, { useCallback, useRef, useState } from "react";
import {
  message,
  Row,
  Col,
  Table,
  Spin,
  Space,
  Card,
  Tag,
  Button,
  Typography,
  Tooltip,
} from "antd";
import {
  SearchOutlined,
  FileTextOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import MotionHoc from "../../../utils/MotionHoc";
import { HEADERS_EXPORT_BEN, POST_LOAD_DTR_PAYMENT } from "../../API/apiUrls";
import CreatePayment from "./modal/CreatePayment";
import { ThaiRangePicker } from "../../../hook/ThaiDatePicker";
import ExportExcelForPayment from "./report/ExportExcelForPayment";

const { Text } = Typography;

const Main = () => {
  const [loading, setLoading] = useState(false);
  const [arrayTable, setArrayTable] = useState([]);
  const [modalState, setModalState] = useState({
    open: false,
    record: null,
  });
  const [dateQuery, setDateQuery] = useState([
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ]);

  const lastPayloadRef = useRef(null);

  // ── โหลดข้อมูล (return mapped เพื่อให้ caller ใช้ต่อ) ──────
  const loadMain = useCallback(async (payload) => {
    setLoading(true);
    try {
      const res = await axios.post(POST_LOAD_DTR_PAYMENT, payload, {
        headers: HEADERS_EXPORT_BEN,
      });

      const raw = res.data?.data;
      if (Array.isArray(raw) && raw.length > 0) {
        const mapped = raw.map((group, idx) => ({ ...group, key: idx + 1 }));
        setArrayTable(mapped);
        return mapped; // ← return เพื่อให้ reloadAndRefreshModal ใช้
      } else {
        setArrayTable([]);
        message.info("ไม่มีข้อมูล");
        return [];
      }
    } catch (error) {
      if (error.name !== "CanceledError") {
        message.error(
          error.response?.data?.message ||
            error.message ||
            "ไม่สามารถโหลดข้อมูลได้",
        );
        setArrayTable([]);
      }
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // ── กดค้นหา ──────────────────────────────────────────────────
  const onQueryData = useCallback(() => {
    if (!dateQuery || dateQuery.length < 2) return;
    const payload = {
      date1: dayjs(dateQuery[0]).format("YYYY-MM-DD"),
      date2: dayjs(dateQuery[1]).format("YYYY-MM-DD"),
    };
    lastPayloadRef.current = payload;
    loadMain(payload);
  }, [dateQuery, loadMain]);

  // ── เปิด modal ────────────────────────────────────────────────
  const openModal = useCallback((record) => {
    setModalState({ open: true, record });
  }, []);

  // ── ปิด modal ─────────────────────────────────────────────────
  const closeModal = useCallback(() => {
    setModalState({ open: false, record: null });
  }, []);

  // ── บันทึก → reload → modal ยังเปิด รับ data ใหม่ ──────────
  const reloadAndRefreshModal = useCallback(async () => {
    if (!lastPayloadRef.current) return;
    const mapped = await loadMain(lastPayloadRef.current);

    setModalState((prev) => {
      if (!prev.open || !prev.record) return prev;
      const updated = mapped.find((g) => g.TEMPBILL === prev.record.TEMPBILL);
      return updated ? { ...prev, record: updated } : prev;
    });
  }, [loadMain]);

  // ── ตั้งบิล → reload → ปิด modal ─────────────────────────────
  const reloadAndClose = useCallback(async () => {
    if (lastPayloadRef.current) {
      await loadMain(lastPayloadRef.current);
    }
    closeModal();
  }, [loadMain, closeModal]);

  // ── columns ──────────────────────────────────────────────────
  const columns = [
    {
      title: "ลำดับ",
      dataIndex: "key",
      key: "key",
      align: "center",
      width: 60,
    },
    {
      title: "เลขที่บิล",
      dataIndex: "TEMPBILL",
      key: "TEMPBILL",
      align: "center",
      width: 160,
      render: (text) => <Text strong>{text ?? "-"}</Text>,
    },
    {
      title: "วันที่",
      key: "dateRound",
      align: "center",
      width: 130,
      render: (_, record) => {
        const date = record.items?.[0]?.dateRound;
        return date ? dayjs(date).format("DD MMM YYYY") : "-";
      },
    },
    {
      title: "ยอดรวม (บาท)",
      key: "grandTotal",
      align: "right",
      width: 150,
      render: (_, record) => {
        // ✅ รวม tr14 + spouseFee + operationFee + familyRegistration + additionalFee
        const total = (record.items ?? []).reduce(
          (sum, i) =>
            sum +
            Number(i.tr14 ?? 0) +
            Number(i.spouseFee ?? 0) +
            Number(i.operationFee ?? 0) +
            Number(i.familyRegistration ?? 0) +
            Number(i.additionalFee ?? 0),
          0,
        );
        return (
          <Text type="danger" strong>
            {total.toLocaleString("th-TH")} ฿
          </Text>
        );
      },
    },
    {
      title: "ยอดหัก ณ ที่จ่าย 3%",
      key: "withheld",
      align: "right",
      width: 150,
      render: (_, record) => {
        // ✅ เฉพาะ operationFee เท่านั้น × 3%
        const operTotal = (record.items ?? []).reduce(
          (sum, i) =>
            sum + Number(i.operationFee ?? 0) + Number(i.additionalFee ?? 0),
          0,
        );
        const withheld = operTotal * 0.03;
        return (
          <Text type="warning" strong>
            {withheld.toLocaleString("th-TH", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}{" "}
            ฿
          </Text>
        );
      },
    },
    {
      title: "ยอดจ่ายสุทธิ (บาท)",
      key: "netPay",
      align: "right",
      width: 150,
      render: (_, record) => {
        const grandTotal = (record.items ?? []).reduce(
          (sum, i) =>
            sum +
            Number(i.tr14 ?? 0) +
            Number(i.spouseFee ?? 0) +
            Number(i.operationFee ?? 0) +
            Number(i.familyRegistration ?? 0) +
            Number(i.additionalFee ?? 0),
          0,
        );

        const withheld =
          (record.items ?? []).reduce(
            (sum, i) => sum + Number(i.operationFee ?? 0),
            0,
          ) * 0.03;

        // ✅ ยอดรวม - หัก ณ ที่จ่าย
        const netPay = grandTotal - withheld;

        return (
          <Text style={{ color: "#27ae60", fontWeight: 700 }}>
            {netPay.toLocaleString("th-TH", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}{" "}
            ฿
          </Text>
        );
      },
    },
    {
      title: "การเงิน",
      key: "approve",
      align: "center",
      width: 120,
      render: (_, record) => {
        const approved = record.items?.[0]?.APPROVE === 1;
        return (
          <Tag
            color={approved ? "success" : "warning"}
            style={{ minWidth: 90, textAlign: "center" }}
          >
            {approved ? "อนุมัติแล้ว" : "รออนุมัติ"}
          </Tag>
        );
      },
    },
    {
      title: "บัญชี",
      key: "approveAcc",
      align: "center",
      width: 120,
      render: (_, record) => {
        const approvedAcc = record.items?.[0]?.APPROVE_ACC === 1;
        return (
          <Tag
            color={approvedAcc ? "success" : "default"}
            style={{ minWidth: 90, textAlign: "center" }}
          >
            {approvedAcc ? "อนุมัติแล้ว" : "รออนุมัติ"}
          </Tag>
        );
      },
    },
    {
      title: "การจัดการ",
      align: "center",
      width: 140,
      render: (_, record) => {
        const approvedAcc = record.items?.[0]?.APPROVE_ACC === 1;
        return (
          <Space size={10}>
            <Tooltip title={approvedAcc ? "ดูรายละเอียด" : "ตั้งเบิก"}>
              <Button
                variant="outlined"
                color={approvedAcc ? "green" : "blue"}
                icon={<FileTextOutlined />}
                onClick={() => openModal(record)}
                size="large"
                style={{ minWidth: 120, borderRadius: 8 }}
              >
                {approvedAcc ? "ดูข้อมูล" : "ตั้งเบิก"}
              </Button>
            </Tooltip>

            {/* <Button
              icon={<FileExcelOutlined />}
              onClick={() => {
                ExportExcelForPayment(record);
                console.log(record);
              }}
              size="large"
              style={{
                minWidth: 120,
                borderRadius: "8px",
                backgroundColor: "#107c41", // สีเขียวออริจินัลของ Microsoft Excel
                borderColor: "#107c41",
                color: "#ffffff",
                boxShadow: "0 4px 10px rgba(16, 124, 65, 0.3)", // เงาสีเขียวอ่อน
                fontWeight: 500,
              }}
            >
              ออก Excel
            </Button> */}
          </Space>
        );
      },
    },
  ];

  // ── render ────────────────────────────────────────────────────
  return (
    <Space
      direction="vertical"
      size="middle"
      style={{ display: "flex", width: "100%" }}
    >
      {/* ── ค้นหา ── */}
      <Card style={{ padding: "16px 24px" }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space size={16}>
              <ThaiRangePicker
                size="large"
                value={dateQuery}
                onChange={setDateQuery}
                placeholder={["วันที่เริ่มต้น", "วันที่สิ้นสุด"]}
                allowClear={false}
              />
              <Button
                type="primary"
                icon={<SearchOutlined />}
                size="large"
                loading={loading}
                disabled={!dateQuery || dateQuery.length < 2}
                onClick={onQueryData}
                style={{
                  borderRadius: 8,
                  fontWeight: "bold",
                  background: "linear-gradient(135deg, #667eea, #3b5bdb)",
                  border: "none",
                  boxShadow: "0 4px 12px rgba(102,126,234,0.4)",
                }}
              >
                ค้นหา
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* ── ตาราง ── */}
      <Card style={{ overflow: "hidden", padding: 0 }}>
        <Spin spinning={loading} size="large" tip="กำลังโหลดข้อมูล...">
          <Table
            size="middle"
            columns={columns}
            dataSource={arrayTable}
            rowKey="key"
            scroll={{ x: 900 }}
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `ทั้งหมด ${total} รายการ`,
            }}
            footer={() => (
              <div style={{ textAlign: "right", paddingRight: 16 }}>
                <Text strong>จำนวนบิลทั้งหมด: </Text>
                <Text type="danger" strong>
                  {arrayTable.length}
                </Text>
              </div>
            )}
          />
        </Spin>

        {modalState.open && (
          <CreatePayment
            open={modalState.open}
            close={closeModal}
            dataLoad={modalState.record}
            onSaveSuccess={reloadAndRefreshModal}
            onApproveSuccess={reloadAndClose}
          />
        )}
      </Card>
    </Space>
  );
};

const MainCreatePaymentInvestigate = MotionHoc(Main);
export default MainCreatePaymentInvestigate;
